/**
 * RiskAssessmentPanel renders a route risk assessment result (the KPI object from
 * POST /routing/assess) into #riskAssessmentPanel in the Route Assessment tab: overall band + score, core
 * KPIs, active disclaimers, assumptions and a per-segment table with expandable details.
 *
 * Pure DOM renderer (Bootstrap 4 markup, collapse plugin for the segment details). Everything dynamic
 * goes through escapeHtml. The permanent liability disclaimer is rendered server-side in the view and
 * therefore skipped here.
 */

import {
  DEFAULT_BANDS,
  LOW_COVERAGE,
  bandFor,
  bandStyle,
  coverageBasisLabel,
  escapeHtml,
  formatKm,
  formatLocalTime,
  formatNumber,
  kpiLabel,
  kpiText,
  disclaimerClass,
  reasonLabel,
  segmentNumber,
} from "./RiskFormat";
import { metricHelp } from "../RouteHelp";

const STALE_ID = "rraStaleNote";
const REPORT_BLOCK_ID = "rraReportBlock";

export class RiskAssessmentPanel {
  /**
   * @param {HTMLElement|null} container
   * @param {{assistantState?: string}} [options] assistantState: 'usable' | 'consent' | 'disabled'
   */
  constructor(container, options = {}) {
    this.container = container;
    this.result = null;
    this.assistantState = options.assistantState || "disabled";
    this.reportHandlers = {};
  }

  clear() {
    this.result = null;
    if (this.container) {
      this.container.innerHTML = "";
    }
  }

  /**
   * @param {string} html
   * @param {string} [className]
   */
  message(html, className = "alert alert-info small") {
    this.result = null;
    if (this.container) {
      this.container.innerHTML = `<div class="${escapeHtml(className)} mb-0">${html}</div>`;
    }
  }

  /**
   * @param {string} text
   */
  error(text) {
    this.message(escapeHtml(text), "alert alert-danger small");
  }

  /**
   * @param {string} text
   */
  loading(text) {
    this.message(
      `<span class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>${escapeHtml(text)}`,
      "alert alert-light small"
    );
  }

  /**
   * Flags a rendered result as possibly outdated (inputs changed since the assessment ran).
   */
  markStale() {
    if (!this.container || !this.result || this.container.querySelector(`#${STALE_ID}`)) {
      return;
    }
    const note = document.createElement("div");
    note.id = STALE_ID;
    note.className = "alert alert-light small py-1 px-2 mb-2";
    note.textContent = "Inputs changed — the result below may be outdated. Run the assessment again.";
    this.container.insertBefore(note, this.container.firstChild);
  }

  /**
   * Opens the detail row of a segment and scrolls it into view.
   * @param {string} segmentId
   */
  expandSegment(segmentId) {
    if (!this.container) {
      return;
    }
    const row = this.container.querySelector(`tr.rra-segment-row[data-segment-id="${CSS.escape(String(segmentId))}"]`);
    if (!row) {
      return;
    }
    const target = row.getAttribute("data-target");
    const details = target ? this.container.querySelector(target) : null;
    if (details) {
      const jq = window.jQuery;
      if (jq && typeof jq.fn.collapse === "function") {
        jq(details).collapse("show");
      } else {
        details.classList.add("show");
      }
    }
    if (typeof row.scrollIntoView === "function") {
      row.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }

  /**
   * @param {{ok: boolean, assessmentId: number, cached: boolean, kpi: object, bands: Array<object>, report?: object}} result
   * @param {{onHover?: (segmentId: string|null) => void, onSelect?: (segmentId: string) => void,
   *          onGenerateReport?: () => void, onConsentRequired?: () => void}} [callbacks]
   */
  render(result, callbacks = {}) {
    if (!this.container) {
      return;
    }
    const kpi = result && result.kpi;
    if (!kpi || !kpi.overallScore) {
      this.error("The assessment response was incomplete.");
      return;
    }
    this.result = result;
    this.reportHandlers = {
      onGenerateReport: typeof callbacks.onGenerateReport === "function" ? callbacks.onGenerateReport : null,
      onConsentRequired: typeof callbacks.onConsentRequired === "function" ? callbacks.onConsentRequired : null,
    };
    const bands = Array.isArray(result.bands) && result.bands.length ? result.bands : DEFAULT_BANDS;
    const segments = Array.isArray(kpi.segments) ? kpi.segments : [];

    this.container.innerHTML = [
      this._summaryCard(kpi, result, bands, segments),
      `<div class="small text-muted mb-2">Assessment counts all visible incidents in the data window, independent of the current map filters.</div>`,
      this._disclaimers(kpi),
      this._assumptions(kpi),
      this._segmentTable(segments, bands),
      `<div class="small text-muted mb-2">■ band colours · dashed = low coverage (missing incidents are not a safety signal) · ○ mandatory stop · ▲ hotspot</div>`,
      `<div id="${REPORT_BLOCK_ID}">${this._reportControls(result.report || null)}</div>`,
      this._traceControls(result.analysisLogUrl),
    ].join("");

    this._bindRows(callbacks);
    this._bindReport();
  }

  /**
   * Updates only the report block (used while polling the generation status).
   * @param {object|null} report {status, error, pdfUrl, unverifiedFigures, mapIncluded}
   */
  setReportState(report) {
    if (!this.container || !this.result) {
      return;
    }
    this.result.report = report;
    const block = this.container.querySelector(`#${REPORT_BLOCK_ID}`);
    if (!block) {
      return;
    }
    block.innerHTML = this._reportControls(report);
    this._bindReport();
  }

  /**
   * Disables the report buttons (route changed / inputs stale).
   */
  disableReport() {
    if (!this.container) {
      return;
    }
    this.container.querySelectorAll(`#${REPORT_BLOCK_ID} button`).forEach((b) => {
      b.disabled = true;
    });
  }

  _reportControls(report) {
    const status = report && report.status ? report.status : null;
    const generate = (label, id = "generateRiskReport", cls = "btn-outline-primary") =>
      `<button type="button" id="${id}" class="btn ${cls} btn-sm btn-block">${label}</button>`;

    if (this.assistantState === "disabled") {
      return `<small class="text-muted d-block mt-1">Report generation (PDF) requires the AI assistant, which is not enabled for your account.</small>`;
    }
    if (this.assistantState === "consent") {
      return (
        generate("Generate report (PDF)", "generateRiskReport", "btn-outline-secondary") +
        `<small class="text-muted d-block mt-1">Enable your AI assistant and accept the privacy policy to generate reports.</small>`
      );
    }
    if (status === "pending") {
      return (
        `<button type="button" class="btn btn-outline-primary btn-sm btn-block" disabled><span class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>Generating report…</button>` +
        `<small class="text-muted d-block mt-1">The language model is writing the document from the KPI object; this can take a few minutes. You can keep using the map.</small>`
      );
    }
    if (status === "done") {
      const pdfUrl = report.pdfUrl ? escapeHtml(report.pdfUrl) : "#";
      const notes = [];
      if (report.unverifiedFigures) {
        notes.push(`${escapeHtml(report.unverifiedFigures)} figure(s) could not be verified and are highlighted in the document`);
      }
      if (report.mapIncluded === false) {
        notes.push("map not available");
      }
      return (
        `<a href="${pdfUrl}" id="downloadRiskReport" class="btn btn-primary btn-sm btn-block" target="_blank" rel="noopener">Download report (PDF)</a>` +
        `<button type="button" id="regenerateRiskReport" class="btn btn-link btn-sm btn-block text-muted">Regenerate report</button>` +
        (notes.length ? `<small class="text-muted d-block">${notes.join(" · ")}.</small>` : "")
      );
    }
    if (status === "error") {
      return (
        `<div class="alert alert-danger small py-1 px-2 mb-1">${escapeHtml(report.error || "Report generation failed.")}</div>` +
        generate("Try again", "generateRiskReport")
      );
    }
    return (
      generate("Generate report (PDF)") +
      `<small class="text-muted d-block mt-1">A language model writes the report from the scores above; every figure is checked against the assessment.</small>`
    );
  }

  /**
   * Debug feature: the server only sends analysisLogUrl for accounts an admin flagged
   * (route_risk_trace_enabled) and only when a trace was stored; nothing renders otherwise.
   * @param {string|undefined} url
   */
  _traceControls(url) {
    if (!url) {
      return "";
    }
    return (
      `<div id="rraTraceBlock" class="mt-2">` +
      `<a href="${escapeHtml(url)}" id="downloadRiskAnalysisLog" class="btn btn-outline-secondary btn-sm btn-block" target="_blank" rel="noopener">Download analysis log</a>` +
      `<small class="text-muted d-block mt-1">Debug: every intermediate value of this run (incident weights, per-segment KPIs, baseline percentiles, disclaimer checks) as text + JSON.</small>` +
      `</div>`
    );
  }

  _bindReport() {
    if (!this.container) {
      return;
    }
    const block = this.container.querySelector(`#${REPORT_BLOCK_ID}`);
    if (!block) {
      return;
    }
    block.querySelectorAll("#generateRiskReport, #regenerateRiskReport").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        if (this.assistantState === "consent") {
          if (this.reportHandlers.onConsentRequired) {
            this.reportHandlers.onConsentRequired();
          }
          return;
        }
        if (this.reportHandlers.onGenerateReport) {
          this.reportHandlers.onGenerateReport();
        }
      });
    });
  }

  _summaryCard(kpi, result, bands, segments) {
    const overall = kpi.overallScore;
    const meta = kpi.reportMeta || {};
    const profile = kpi.routeProfile || {};
    const kpis = kpi.kpis || {};
    const band = bandFor(overall.value, bands, overall.band);
    const score = Math.round(Number(overall.value));
    const lowConfidence = overall.confidence === "low";

    const scoreHtml = lowConfidence
      ? `<small class="text-muted ml-2">score ${escapeHtml(score)} · low data confidence</small>`
      : `<span class="rra-score">${escapeHtml(score)}</span><small class="text-muted">/100</small>`;

    const metaParts = [
      `Confidence: ${escapeHtml(overall.confidence || "n/a")}${metricHelp("Confidence")}`,
      result.cached ? "cached" : null,
      meta.reportId ? escapeHtml(meta.reportId) : null,
    ].filter(Boolean);

    const threat = kpis.threat || {};
    const vulnerability = kpis.vulnerability || {};
    const density = kpiText(threat.incident_density, (raw) => `${raw.toFixed(1)} weighted / 100 km`);
    const coverage = kpiText(vulnerability.secure_parking_coverage, (raw) => `${Math.round(raw <= 1 ? raw * 100 : raw)} %`);

    const countries = Array.isArray(profile.countries) ? profile.countries.map(escapeHtml).join(" › ") : "";
    const routeLine = [
      formatKm(profile.distanceKm),
      Number.isFinite(Number(profile.estimatedDurationH)) ? `${formatNumber(profile.estimatedDurationH, 1)} h driving` : null,
      profile.departureLocal && profile.arrivalLocal
        ? `${formatLocalTime(profile.departureLocal, true)} → ${formatLocalTime(profile.arrivalLocal, true)}${Number.isFinite(Number(profile.elapsedH)) ? ` (${formatNumber(profile.elapsedH, 1)} h incl. breaks)` : ""}`
        : null,
      countries || null,
    ]
      .filter(Boolean)
      .join(" · ");

    const window = meta.dataWindow || {};
    const windowLine = [
      window.from && window.to ? `${escapeHtml(window.from)} – ${escapeHtml(window.to)}` : null,
      Number.isFinite(Number(meta.corridorBufferMeters)) ? `corridor ${formatNumber(Number(meta.corridorBufferMeters) / 1000, 1)} km` : null,
    ]
      .filter(Boolean)
      .join(" · ");

    const rows = [
      ["Incident density", density],
      ["Safe parking coverage", coverage],
      ["Driving exposure", this._exposureText(profile.exposure)],
      ["Weakest mandatory-stop segment", this._weakestMandatory(overall, meta, segments)],
      ["Dominant factor", escapeHtml(overall.dominantFactor || "n/a")],
      ["Route", escapeHtml(routeLine) || "n/a"],
      ["Data window / corridor", windowLine || "n/a"],
    ]
      .map(([label, value]) => `<dt class="col-5 font-weight-normal text-muted">${label}${metricHelp(label)}</dt><dd class="col-7 mb-1">${value}</dd>`)
      .join("");

    return `
      <div class="card rra-card mb-2" style="border-left:6px solid ${escapeHtml(band.color)};">
        <div class="card-body p-2">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="badge rra-band-badge${lowConfidence ? " rra-band-badge-lg" : ""}" style="${escapeHtml(bandStyle(band))}">${escapeHtml(band.name)}</span>
              ${scoreHtml}${metricHelp("Risk score")}
            </div>
            <small class="text-muted text-right">${metaParts.join(" · ")}</small>
          </div>
          <div class="progress mt-2" style="height:6px;">
            <div class="progress-bar" role="progressbar" style="width:${Math.max(0, Math.min(100, score))}%;background-color:${escapeHtml(band.color)};" aria-valuenow="${score}" aria-valuemin="0" aria-valuemax="100"></div>
          </div>
          <dl class="row small mb-0 mt-2">${rows}</dl>
        </div>
      </div>`;
  }

  _weakestMandatory(overall, meta, segments) {
    const id = overall.weakestMandatorySegmentId;
    if (!id) {
      return meta.singleSegmentRoute ? "single segment" : "none";
    }
    const segment = segments.find((s) => s.segmentId === id);
    if (!segment) {
      return escapeHtml(id);
    }
    const n = segmentNumber(segment.segmentId, segments.indexOf(segment) + 1);
    return escapeHtml(
      `Segment ${n} (km ${Math.round(Number(segment.fromKm))}–${Math.round(Number(segment.toKm))}, score ${Math.round(Number(segment.segmentScore))})`
    );
  }

  _disclaimers(kpi) {
    const list = Array.isArray(kpi.disclaimers) ? kpi.disclaimers.filter((d) => d && d.severity !== "permanent") : [];
    if (!list.length) {
      return "";
    }
    return list
      .map(
        (d) =>
          `<div class="${disclaimerClass(d.severity)} small py-1 px-2 mb-1"><span class="badge badge-dark">${escapeHtml(d.code)}</span> ${escapeHtml(d.message)} <small>(${escapeHtml(d.scope || "route")})</small></div>`
      )
      .join("");
  }

  _assumptions(kpi) {
    const assumptions = kpi.routeInput && Array.isArray(kpi.routeInput.assumptions) ? kpi.routeInput.assumptions : [];
    if (!assumptions.length) {
      return "";
    }
    return `<ul class="small text-muted pl-3 mb-2">${assumptions.map((a) => `<li>${escapeHtml(a)}</li>`).join("")}</ul>`;
  }

  _segmentTable(segments, bands) {
    if (!segments.length) {
      return "";
    }
    const anyTime = segments.some((segment) => segment.timing && segment.timing.startLocal && segment.timing.endLocal);
    const rows = segments
      .map((segment, index) => {
        const n = segmentNumber(segment.segmentId, index + 1);
        const band = bandFor(segment.segmentScore, bands, segment.band);
        const cov = Number(segment.coverageFactor);
        const covKnown = Number.isFinite(cov);
        const covTitle = covKnown ? `coverage ${cov.toFixed(2)}${segment.coverageBasis ? ` (${coverageBasisLabel(segment.coverageBasis)})` : ""}` : "coverage unknown";
        const covDot = `<span class="rra-cov-dot" style="opacity:${covKnown ? Math.max(0.15, cov) : 0.15};"></span>`;
        const confidence = segment.confidence || {};
        const psrCount = Array.isArray(segment.psrFacilities) ? segment.psrFacilities.length : 0;
        const detailId = `rraSeg-${n}`;
        const covId = `rraCov-${n}`;
        const covCell = segment.coverageBreakdown
          ? `<a href="#${covId}" class="rra-factor-toggle rra-cov-toggle text-nowrap" data-detail="#${detailId}" data-target="#${covId}" role="button" title="${escapeHtml(covTitle)} – click to show how it was derived">${covDot} ${covKnown ? cov.toFixed(2) : "n/a"}</a>`
          : `<span class="text-nowrap" title="${escapeHtml(covTitle)}">${covDot} ${covKnown ? cov.toFixed(2) : "n/a"}</span>`;
        const timing = segment.timing || {};
        const timeCell = this._timeText(timing);
        return `
          <tr class="rra-segment-row" data-segment-id="${escapeHtml(segment.segmentId)}" data-toggle="collapse" data-target="#${detailId}" title="Click for details">
            <td>${escapeHtml(n)}${segment.isMandatoryStop ? " ○" : ""}</td>
            <td>${Math.round(Number(segment.fromKm))}–${Math.round(Number(segment.toKm))}</td>
            ${anyTime ? `<td class="text-nowrap">${timeCell}</td>` : ""}
            <td><span class="badge" style="${escapeHtml(bandStyle(band))}">${escapeHtml(band.name)}</span></td>
            <td>${escapeHtml(Math.round(Number(segment.segmentScore)))}</td>
            <td>${covCell}</td>
            <td>${escapeHtml(confidence.level || "n/a")}</td>
            <td>${escapeHtml(psrCount)}</td>
          </tr>
          <tr class="collapse" id="${detailId}"><td colspan="${anyTime ? 8 : 7}" class="bg-light">${this._segmentDetails(segment, confidence)}</td></tr>`;
      })
      .join("");

    return `
      <div class="table-responsive">
        <table class="table table-sm small rra-segments mb-2">
          <thead><tr><th>#${metricHelp("Segment")}</th><th>km${metricHelp("km")}</th>${anyTime ? `<th>Time${metricHelp("Time")}</th>` : ""}<th>Band${metricHelp("Band")}</th><th>Score${metricHelp("Score")}</th><th>Cov.${metricHelp("Reporting coverage")}</th><th>Conf.${metricHelp("Confidence")}</th><th>🅿${metricHelp("Secure parkings")}</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;
  }

  /**
   * "08:00 – 09:40" (with the weekday when the segment crosses midnight); "" when no departure time.
   *
   * @param {{startLocal?: string|null, endLocal?: string|null}} timing
   * @returns {string} HTML
   */
  _timeText(timing) {
    const start = timing && timing.startLocal;
    const end = timing && timing.endLocal;
    if (!start || !end) {
      return "";
    }
    const sameDay = String(start).slice(0, 10) === String(end).slice(0, 10);
    return escapeHtml(`${formatLocalTime(start, !sameDay)} – ${formatLocalTime(end, !sameDay)}`);
  }

  /**
   * Route-level driving exposure line: motorway share and night share, or what is missing.
   *
   * @param {{roadKnown?: boolean, timeKnown?: boolean, motorwayShare?: number|null, nightShare?: number|null, nightWindow?: string}|undefined} exposure
   * @returns {string} HTML
   */
  _exposureText(exposure) {
    if (!exposure) {
      return "n/a";
    }
    const pct = (v) => `${Math.round(Number(v) * 100)} %`;
    const parts = [];
    parts.push(exposure.roadKnown && Number.isFinite(Number(exposure.motorwayShare)) ? `motorway ${pct(exposure.motorwayShare)}` : "road class unknown");
    parts.push(
      exposure.timeKnown && Number.isFinite(Number(exposure.nightShare))
        ? `night driving ${pct(exposure.nightShare)}${exposure.nightWindow ? ` (${exposure.nightWindow})` : ""}`
        : "departure time not set"
    );
    return escapeHtml(parts.join(" · "));
  }

  _segmentDetails(segment, confidence) {
    const factor = (value) => (Number.isFinite(Number(value)) ? Math.round(Number(value)) : "n/a");
    const parts = [];

    const breakdown = segment.factorBreakdown || null;
    const n = segmentNumber(segment.segmentId, 0);
    const factorHtml = (key, label, value) => {
      const text = `${label} ${escapeHtml(factor(value))}`;
      if (!breakdown || !breakdown[key]) {
        return text + metricHelp(label);
      }
      const id = `rraCalc-${n}-${key}`;
      return `<a href="#${id}" class="rra-factor-toggle" data-toggle="collapse" data-target="#${id}" role="button" title="Show how this value was computed">${text}</a>${metricHelp(label)}`;
    };
    parts.push(
      `<div>${factorHtml("threat", "Threat", segment.threatScore)} · ${factorHtml("vulnerability", "Vulnerability", segment.vulnerabilityScore)} · ${factorHtml("impact", "Impact", segment.impactScore)}</div>`
    );
    if (breakdown) {
      ["threat", "vulnerability", "impact"].forEach((key) => {
        if (breakdown[key]) {
          parts.push(`<div class="collapse rra-calc" id="rraCalc-${n}-${key}">${this._factorCalculation(key, breakdown, segment)}</div>`);
        }
      });
    }
    const timing = segment.timing || {};
    if (timing.startLocal && timing.endLocal) {
      const elapsed =
        Number.isFinite(Number(timing.elapsedStartH)) && Number.isFinite(Number(timing.elapsedEndH))
          ? ` · ${formatNumber(timing.elapsedStartH, 1)}–${formatNumber(timing.elapsedEndH, 1)} h after departure`
          : "";
      parts.push(`<div class="text-muted">Local time: ${escapeHtml(`${formatLocalTime(timing.startLocal, true)} – ${formatLocalTime(timing.endLocal, true)}`)}${escapeHtml(elapsed)}</div>`);
    }
    const exposure = segment.exposure;
    if (exposure) {
      const pct = (v) => `${Math.round(Number(v) * 100)} %`;
      const line = [
        exposure.roadClassKnown && Number.isFinite(Number(exposure.motorwayShare)) ? `motorway ${pct(exposure.motorwayShare)}` : null,
        exposure.timeKnown && Number.isFinite(Number(exposure.nightShare)) ? `night ${pct(exposure.nightShare)}` : null,
        Number.isFinite(Number(exposure.multiplier)) ? `threat ×${Number(exposure.multiplier).toFixed(2)}` : null,
        exposure.capApplied ? "daytime motorway cap applied" : null,
      ].filter(Boolean);
      if (line.length) {
        parts.push(`<div class="text-muted">Exposure: ${line.map(escapeHtml).join(" · ")}</div>`);
      }
    }
    const covValue = Number(segment.coverageFactor);
    const covText = `Coverage ${Number.isFinite(covValue) ? covValue.toFixed(2) : "n/a"}${segment.coverageBasis ? ` (${escapeHtml(coverageBasisLabel(segment.coverageBasis))})` : ""}`;
    if (segment.coverageBreakdown) {
      const covId = `rraCov-${n}`;
      parts.push(
        `<div class="text-muted"><a href="#${covId}" class="rra-factor-toggle" data-toggle="collapse" data-target="#${covId}" role="button" title="Show how the coverage factor was derived">${covText}</a></div>` +
          `<div class="collapse rra-calc" id="${covId}">${this._coverageCalculation(segment.coverageBreakdown)}</div>`
      );
    } else {
      parts.push(`<div class="text-muted">${covText}</div>`);
    }
    const sample = [
      confidence.sampleSize !== undefined && confidence.sampleSize !== null ? `sample size ${escapeHtml(confidence.sampleSize)}` : null,
      confidence.note ? escapeHtml(confidence.note) : null,
    ].filter(Boolean);
    if (sample.length) {
      parts.push(`<div class="text-muted">${sample.join(" · ")}</div>`);
    }
    if (Array.isArray(segment.notApplicableKpis) && segment.notApplicableKpis.length) {
      parts.push(`<div class="text-muted">Not applicable: ${segment.notApplicableKpis.map(escapeHtml).join(", ")}</div>`);
    }

    const stops = Array.isArray(segment.mandatoryStops) ? segment.mandatoryStops : [];
    if (stops.length) {
      const items = stops.map((stop) => {
        const psr = stop.nearestPsrM !== null && stop.nearestPsrM !== undefined ? `nearest PSR ${formatNumber(Number(stop.nearestPsrM) / 1000, 1)} km` : "nearest PSR: none";
        const arrival = stop.atLocal ? `arrives ${formatLocalTime(stop.atLocal, true)}` : null;
        const line = [`km ${Math.round(Number(stop.atKm))}`, arrival, reasonLabel(stop.reason), psr, stop.inHotspot ? "in hotspot" : null].filter(Boolean);
        return `<li>${line.map(escapeHtml).join(" · ")}</li>`;
      });
      parts.push(`<div class="mt-1"><strong>Mandatory stops</strong><ul class="pl-3 mb-1">${items.join("")}</ul></div>`);
    }

    const psr = Array.isArray(segment.psrFacilities) ? segment.psrFacilities : [];
    if (psr.length) {
      const items = psr.map((facility) => {
        const line = [
          facility.name || `PSR #${facility.id}`,
          facility.levelId !== undefined && facility.levelId !== null ? `level ${facility.levelId}` : null,
          Number.isFinite(Number(facility.distanceFromRouteM)) ? `${formatNumber(Number(facility.distanceFromRouteM) / 1000, 1)} km from route` : null,
        ].filter(Boolean);
        return `<li>${line.map(escapeHtml).join(" · ")}</li>`;
      });
      parts.push(`<div class="mt-1"><strong>Secure parkings</strong><ul class="pl-3 mb-1">${items.join("")}</ul></div>`);
    }

    const incidents = Array.isArray(segment.incidentRefs) ? segment.incidentRefs : [];
    if (incidents.length) {
      const links = incidents.map((id) => {
        const safeId = encodeURIComponent(String(id));
        return `<a href="#" class="markerPopupButton" data-href="/incident/view?id=${safeId}">#${escapeHtml(id)}</a>`;
      });
      parts.push(`<div class="mt-1"><strong>Incidents</strong> (${incidents.length}): ${links.join(", ")}</div>`);
    }

    return parts.join("");
  }

  /**
   * Computation path of one factor score: the KPI table (value, normalised 0–100, weight,
   * re-normalised weight, contribution), the weighted mean, for threat the exposure
   * multiplier, and finally how the factor enters the segment score.
   *
   * @param {"threat"|"vulnerability"|"impact"} key
   * @param {object} breakdown segment.factorBreakdown
   * @param {object} segment
   * @returns {string} HTML
   */
  _factorCalculation(key, breakdown, segment) {
    const fb = breakdown[key];
    const num = (v, digits = 1) => (Number.isFinite(Number(v)) ? formatNumber(Number(v), digits) : "–");
    const rawText = (row) => {
      if (row.applicable === false || row.raw === null || row.raw === undefined) {
        return `<span class="text-muted" title="${escapeHtml(row.gateReason || "not applicable")}">n/a${row.gateReason ? ` (${escapeHtml(String(row.gateReason).replace(/_/g, " "))})` : ""}</span>`;
      }
      const unit = row.unit ? ` ${escapeHtml(String(row.unit).replace(/_/g, " "))}` : "";
      return `${escapeHtml(num(row.raw, Number(row.raw) >= 100 ? 0 : 2))}${unit}`;
    };
    const rows = Object.keys(fb.kpis || {})
      .map((kpiKey) => {
        const row = fb.kpis[kpiKey];
        const counted = row.applicable !== false && Number(row.weight) > 0;
        const method = row.method && row.method !== "n/a" ? ` <span class="text-muted">(${escapeHtml(row.method)}${Number.isFinite(Number(row.sampleSize)) && row.sampleSize > 0 ? `, n=${escapeHtml(row.sampleSize)}` : ""})</span>` : "";
        return `<tr class="${counted ? "" : "text-muted"}">
          <td>${escapeHtml(kpiLabel(kpiKey))}${metricHelp(kpiLabel(kpiKey))}${method}</td>
          <td class="text-right">${rawText(row)}</td>
          <td class="text-right">${counted ? escapeHtml(num(row.normalized, 1)) : "–"}</td>
          <td class="text-right">${escapeHtml(num(row.weight, 2))}${counted && Number.isFinite(Number(row.effectiveWeight)) ? ` <span class="text-muted">→ ${escapeHtml(num(Number(row.effectiveWeight) * 100, 0))} %</span>` : ""}</td>
          <td class="text-right">${counted ? escapeHtml(num(row.contribution, 1)) : "–"}</td>
        </tr>`;
      })
      .join("");
    const lines = [];
    lines.push(`Weighted mean of the applicable KPIs (weights re-normalised to ${escapeHtml(num(fb.weightSum, 2))}) = <strong>${escapeHtml(num(fb.score, 1))}</strong>`);
    if (key === "threat" && Number.isFinite(Number(fb.exposureMultiplier))) {
      const ex = segment.exposure || {};
      const why = [
        ex.roadClassKnown && Number.isFinite(Number(ex.motorwayShare)) ? `motorway ${Math.round(Number(ex.motorwayShare) * 100)} %` : "road class unknown",
        ex.timeKnown && Number.isFinite(Number(ex.nightShare)) ? `night ${Math.round(Number(ex.nightShare) * 100)} %` : "time of day unknown",
      ].join(", ");
      lines.push(
        `× driving exposure ${escapeHtml(num(fb.exposureMultiplier, 2))} (${escapeHtml(why)}) = <strong>${escapeHtml(num(fb.adjustedScore, 1))}</strong>`
      );
    }
    const seg = breakdown.segment || {};
    const used = seg.weightsUsed || {};
    const scores = { threat: fb.adjustedScore !== undefined ? fb.adjustedScore : segment.threatScore, vulnerability: segment.vulnerabilityScore, impact: segment.impactScore };
    const terms = Object.keys(used)
      .map((f) => `${escapeHtml(num(used[f], 2))} × ${escapeHtml(f)} ${escapeHtml(num(f === key ? (key === "threat" ? fb.adjustedScore : fb.score) : scores[f], 1))}`)
      .join(" + ");
    if (terms) {
      lines.push(`Segment score = ${terms} = <strong>${escapeHtml(num(seg.scoreUncapped, 1))}</strong>${seg.capApplied ? `, capped to <strong>${escapeHtml(num(seg.capMaxScore, 0))}</strong> (daytime motorway without mandatory stop)` : ""}`);
    }
    return `
      <table class="table table-sm table-borderless rra-calc-table mb-1">
        <thead><tr><th>KPI</th><th class="text-right">Value${metricHelp("Value")}</th><th class="text-right">0–100${metricHelp("0–100")}</th><th class="text-right">Weight${metricHelp("Weight")}</th><th class="text-right">Contrib.${metricHelp("Contrib.")}</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="rra-calc-lines">${lines.map((l) => `<div>${l}</div>`).join("")}</div>`;
  }

  /**
   * Derivation of the segment's coverage factor (§5.9): the country prior (baseline row of
   * every country the segment touches as a length-weighted mean, or the route-level fallback
   * cascade when no baseline row exists), the segment-local reporting evidence of the corridor
   * (strongest of reporter diversity, reporting volume, secure-parking presence) and the blend
   * of both. Coverage only feeds confidence, the LOW_COVERAGE disclaimer and the map style,
   * never the threat score.
   *
   * @param {object} cb segment.coverageBreakdown
   * @returns {string} HTML
   */
  _coverageCalculation(cb) {
    const num = (v, digits = 2) => (Number.isFinite(Number(v)) ? formatNumber(Number(v), digits) : "–");
    const refs = cb.references || {};
    const result = cb.result || {};
    const prior = cb.prior || {};
    const priorResult = prior.result || {};
    const local = cb.local || {};
    const w = Number(cb.localWeight);
    const lines = [];
    let table = "";

    // 1. Country prior
    if (prior.method === "baseline_countries") {
      const rows = (prior.countries || [])
        .map((c) => {
          const known = c.coverageFactor !== null && c.coverageFactor !== undefined;
          const inputs = [
            c.reporterDiversity !== null && c.reporterDiversity !== undefined ? `${escapeHtml(c.reporterDiversity)} reporters` : null,
            c.facilityDensityPer1000km2 !== null && c.facilityDensityPer1000km2 !== undefined ? `${escapeHtml(num(c.facilityDensityPer1000km2, 2))} facilities/1000 km²` : null,
            c.incidentCount !== null && c.incidentCount !== undefined
              ? `${escapeHtml(c.incidentCount)} incidents in ${escapeHtml(c.activeMonths ?? "–")}/${escapeHtml(c.windowMonths ?? "–")} months`
              : null,
          ].filter(Boolean);
          return `<tr class="${known ? "" : "text-muted"}">
            <td>${escapeHtml(c.iso2)}</td>
            <td class="text-right">${escapeHtml(num(c.overlapKm, 1))} km${known && Number.isFinite(Number(c.share)) ? ` <span class="text-muted">(${escapeHtml(Math.round(Number(c.share) * 100))} %)</span>` : ""}</td>
            <td class="text-right">${known ? escapeHtml(num(c.coverageFactor, 2)) : '<span title="no baseline row – not counted">n/a</span>'}</td>
            <td>${known ? escapeHtml(coverageBasisLabel(c.coverageBasis)) : "–"}</td>
            <td class="text-muted">${inputs.map(escapeHtml).join(" · ")}</td>
          </tr>`;
        })
        .join("");
      table = `
        <table class="table table-sm table-borderless rra-calc-table mb-1">
          <thead><tr><th>Country</th><th class="text-right">In segment</th><th class="text-right">Coverage</th><th>Basis</th><th>Baseline inputs</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>`;
      lines.push(
        `<strong>Country prior</strong> = length-weighted mean of the countries with a baseline row = <strong>${escapeHtml(num(priorResult.factor, 2))}</strong> (basis by majority of length: ${escapeHtml(coverageBasisLabel(priorResult.basis))})`
      );
    } else {
      const fb = prior.fallback || {};
      const isoList = (prior.countries || []).map((c) => c.iso2).filter(Boolean);
      lines.push(
        `<strong>Country prior</strong>: ${isoList.length ? `no baseline row for ${escapeHtml(isoList.join(", "))}` : "country attribution unavailable"} – route-level fallback (whole route, not this segment):`
      );
      if (fb.facilityDensityPer1000km2 !== null && fb.facilityDensityPer1000km2 !== undefined) {
        lines.push(
          `${escapeHtml(fb.psrInCorridor)} secure parkings in the corridor / ${escapeHtml(num(fb.corridorAreaKm2, 0))} km² = ${escapeHtml(num(fb.facilityDensityPer1000km2, 3))} per 1000 km²; min(1, ${escapeHtml(num(fb.facilityDensityPer1000km2, 3))} / ${escapeHtml(num(fb.facilityRefPer1000km2, 2))}) = <strong>${escapeHtml(num(priorResult.factor, 2))}</strong>`
        );
      } else {
        lines.push(
          `No secure parking in the corridor → reporting volume: continuity min(1, ${escapeHtml(fb.activeMonths ?? "–")} / ${escapeHtml(fb.windowMonths ?? "–")} months) = ${escapeHtml(num(fb.continuity, 2))}; volume min(1, ${escapeHtml(fb.incidentCount ?? "–")} / ${escapeHtml(num(fb.volumeRef, 0))}); product = <strong>${escapeHtml(num(priorResult.factor, 2))}</strong>`
        );
      }
    }
    const basisFormula = {
      reporter_diversity: `reporter diversity: min(1, log(1 + reporters) / log(1 + ${num(refs.reporterRef, 0)})), used from ${num(refs.reporterMin, 0)} reporters`,
      facility_density: `facility density: min(1, facilities per 1000 km² / ${num(refs.facilityRefPer1000km2, 2)})`,
      volume_continuity: `reporting volume: min(1, active months / window months) × min(1, incidents / ${num(refs.volumeRef, 0)})`,
    };
    if (priorResult.basis && basisFormula[priorResult.basis]) {
      lines.push(`<span class="text-muted">Country basis formula – ${escapeHtml(basisFormula[priorResult.basis])}</span>`);
    }

    // 2. Segment-local evidence
    if (local && local.scores) {
      const sc = local.scores || {};
      const mark = (key) => (key === local.basis ? " ←" : "");
      lines.push(
        `<strong>Local evidence</strong> in this segment's corridor (all reported incidents in the data window, strongest signal counts): ` +
          `reporter diversity min(1, log(1 + ${escapeHtml(local.reporterDiversity ?? "–")}) / log(1 + ${escapeHtml(num(local.reporterRef, 0))})) = ${escapeHtml(num(sc.reporter_diversity, 2))}${mark("reporter_diversity")}; ` +
          `reporting volume min(1, ${escapeHtml(local.incidentCount ?? "–")} / ${escapeHtml(num(local.volumeRef, 0))}) = ${escapeHtml(num(sc.volume_continuity, 2))}${mark("volume_continuity")}; ` +
          `secure parkings min(1, ${escapeHtml(local.psrInCorridor ?? "–")} / ${escapeHtml(num(local.psrRef, 0))}) = ${escapeHtml(num(sc.facility_density, 2))}${mark("facility_density")} ` +
          `→ <strong>${escapeHtml(num(local.factor, 2))}</strong> (${escapeHtml(coverageBasisLabel(local.basis))})`
      );
    }

    // 3. Blend
    if (Number.isFinite(w)) {
      lines.push(
        `<strong>Segment coverage</strong> = (1 − ${escapeHtml(num(w, 2))}) × ${escapeHtml(num(priorResult.factor, 2))} + ${escapeHtml(num(w, 2))} × ${escapeHtml(num(local.factor, 2))} = <strong>${escapeHtml(num(result.factor, 2))}</strong>${w > 0 ? " (basis = strongest local signal)" : " (local evidence disabled)"}`
      );
    }
    const thresholds = [
      Number.isFinite(Number(refs.mediumCoverage)) ? `medium confidence from ${num(refs.mediumCoverage, 2)}` : null,
      Number.isFinite(Number(refs.highCoverage)) ? `high from ${num(refs.highCoverage, 2)}` : null,
      `dashed on the map below ${num(LOW_COVERAGE, 2)}`,
    ].filter(Boolean);
    lines.push(`<span class="text-muted">Coverage feeds confidence and disclaimers only (${escapeHtml(thresholds.join(", "))}); it never changes the threat score.</span>`);
    return `${table}<div class="rra-calc-lines">${lines.map((l) => `<div>${l}</div>`).join("")}</div>`;
  }

  _bindRows(callbacks) {
    const onHover = typeof callbacks.onHover === "function" ? callbacks.onHover : null;
    const onSelect = typeof callbacks.onSelect === "function" ? callbacks.onSelect : null;
    const jq = window.jQuery;
    const hasCollapse = !!(jq && typeof jq.fn.collapse === "function");
    this.container.querySelectorAll("tr.rra-segment-row").forEach((row) => {
      const segmentId = row.getAttribute("data-segment-id");
      if (onHover) {
        row.addEventListener("mouseenter", () => onHover(segmentId));
        row.addEventListener("mouseleave", () => onHover(null));
      }
      if (onSelect) {
        // The Bootstrap collapse plugin handles the actual toggle via data-toggle; this only notifies.
        row.addEventListener("click", () => onSelect(segmentId));
      }
      // Coverage value: opens the segment details (if closed) and toggles the coverage derivation
      // instead of toggling the whole row.
      row.querySelectorAll("a.rra-cov-toggle").forEach((link) => {
        link.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          const details = this.container.querySelector(link.getAttribute("data-detail") || "");
          const calc = this.container.querySelector(link.getAttribute("data-target") || "");
          if (details) {
            if (hasCollapse) {
              jq(details).collapse("show");
            } else {
              details.classList.add("show");
            }
          }
          if (calc) {
            if (hasCollapse) {
              jq(calc).collapse("toggle");
            } else {
              calc.classList.toggle("show");
            }
          }
          if (onSelect) {
            onSelect(segmentId);
          }
        });
      });
    });
  }
}
